/** @format */

import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { ToastrModule } from 'ngx-toastr';

import { AppComponent } from './app.component';
import { environment } from '../environments/environment';

import * as fromContainers from './containers';
import * as fromComponents from './components';
import * as fromPipes from './pipes';
import * as fromStore from './store';
import * as fromService from './services';

import { ActionReducer, MetaReducer, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { storeLogger } from 'ngrx-store-logger';

import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import {
	MsalBroadcastService,
	MsalGuard,
	MsalGuardConfiguration,
	MsalInterceptor,
	MsalInterceptorConfiguration,
	MsalModule,
	MsalRedirectComponent,
	MsalService,
	MSAL_GUARD_CONFIG,
	MSAL_INSTANCE,
	MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';
import { RoleGuard } from './guards/role.guard';
import { NgChartsModule } from 'ng2-charts';
import { NgbTooltipModule, NgbDropdownModule, NgbDatepickerModule, NgbModule, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

export function logger(reducer: ActionReducer<any>): any {
	return storeLogger()(reducer);
}

export const roles = {
	AppAccess: 'AppAccess',
	DelegatedManager: 'DelegatedManager',
	TimeKeepers: 'TimeKeepers',
	Manager: 'Manager',
	HR: 'HR',
	Admin: 'Admin',
};

const routes: any[] = [
	{
		path: '',
		pathMatch: 'full',
		redirectTo: 'year-at-a-glance',
	},
	{
		path: 'year-at-a-glance',
		component: fromContainers.YearAtAGlanceComponent,
		pathMatch: 'full',
		canActivate: [RoleGuard],
		data: { expectedRoles: [roles.AppAccess] },
	},
	{
		path: 'timesheet/:id',
		component: fromContainers.TimesheetComponent,
		canActivate: [RoleGuard],
		pathMatch: 'full',
		data: { expectedRoles: [roles.AppAccess] },
	},
	{
		path: 'timesheet',
		component: fromContainers.TimesheetComponent,
		canActivate: [RoleGuard],
		pathMatch: 'full',
		data: { expectedRoles: [roles.AppAccess] },
	},
	{
		path: 'profile',
		component: fromContainers.ProfilePageComponent,
		canActivate: [RoleGuard],
		data: { expectedRoles: [roles.AppAccess] },
	},
	{
		path: 'team-profiles',
		component: fromContainers.TeamProfilesComponent,
		canActivate: [RoleGuard],
		data: { expectedRoles: [roles.Manager, roles.TimeKeepers] },
	},
	{
		path: 'email-scheduler',
		component: fromContainers.NotificationsComponent,
		canActivate: [RoleGuard],
		data: { expectedRoles: [roles.Manager, roles.TimeKeepers] },
	},
	{
		path: 'approvals',
		component: fromContainers.ApprovalsComponent,
		canActivate: [RoleGuard],
		data: { expectedRoles: [roles.Manager] },
	},
	{
		path: 'team',
		component: fromContainers.ApprovalsComponent,
		canActivate: [RoleGuard],
		data: { expectedRoles: [roles.Manager, roles.TimeKeepers] },
	},
	{
		path: 'acting-scheduler',
		component: fromContainers.ScheduledActingComponent,
		canActivate: [RoleGuard],
		data: { expectedRoles: [roles.Manager, roles.TimeKeepers] },
	},
	{
		path: 'posting',
		component: fromContainers.PostingsComponent,
		canActivate: [RoleGuard],
		data: { expectedRoles: [roles.TimeKeepers, roles.Manager] },
	},
	{
		path: 'delegations',
		component: fromContainers.DelegationsComponent,
		canActivate: [RoleGuard],
		data: { expectedRoles: [roles.Manager, roles.TimeKeepers] },
	},
	{
		path: 'accounts',
		component: fromContainers.AccountsPanel,
		canActivate: [RoleGuard],
		data: { expectedRoles: [roles.Admin] },
	},

	//Using MsalGuard because you should at least be a DNV employee to see accessdenied
	//This component also adds your user info into DB
	{
		path: 'accessdenied',
		component: fromContainers.AccessDenidedComponent,
		canActivate: [MsalGuard],
	},
	{ path: 'logout', component: fromComponents.LogoutComponent },
];

const metaReducers: MetaReducer<any>[] = environment.production ? [] : [logger];
const reducers = {
	...fromStore.reducers,
};

// const isIE =
//   window.navigator.userAgent.indexOf('MSIE ') > -1 ||
//   window.navigator.userAgent.indexOf('Trident/') > -1; // Remove this line to use Angular Universal

export function loggerCallback(logLevel: LogLevel, message: string) {
	console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
	return new PublicClientApplication({
		auth: {
			clientId: environment.auth.clientId, // PPE testing environment
			authority: environment.auth.authority, // PPE testing environment.
			redirectUri: environment.auth.redirectUri,
			postLogoutRedirectUri: environment.auth.postLogoutRedirectUri,
			navigateToLoginRequestUrl: true, // allow redirect to the link you came from if you were asked to login.
		},
		cache: {
			cacheLocation: BrowserCacheLocation.LocalStorage,
			// storeAuthStateInCookie: isIE, // set to true for IE 11. Remove this line to use Angular Universal
			storeAuthStateInCookie: false,
		},
		system: {
			loggerOptions: {
				loggerCallback,
				logLevel: LogLevel.Info,
				piiLoggingEnabled: false,
			},
		},
	});
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
	const protectedResourceMap = new Map<string, Array<string>>();
	protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
	protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['groupmember.read.all']);
	protectedResourceMap.set('/api/', [`api://${environment.auth.clientId}/access-user`]);
	return {
		interactionType: InteractionType.Redirect,
		protectedResourceMap,
	};
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		authRequest: {
			scopes: ['user.read'],
		},
		loginFailedRoute: '/accessdenied',
	};
}

@NgModule({
	declarations: [AppComponent, ...fromContainers.containers, ...fromComponents.components, ...fromPipes.pipes],
	imports: [
		BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
		HttpClientModule,
		FormsModule,
		ReactiveFormsModule,
		RouterModule.forRoot(routes),
		StoreModule.forRoot(reducers, { metaReducers }),
		EffectsModule.forRoot([fromStore.effects]),
		// MsalModule,
		NgChartsModule,
		NgSelectModule,
		NgbTooltipModule,
		BrowserAnimationsModule,
		ToastrModule.forRoot({
			positionClass: 'toast-bottom-right',
			preventDuplicates: false,
			timeOut: 10000,
			progressBar: true,
			enableHtml: true,
			// closeButton: true,
		}),
		NgbModule,
		NgbDropdownModule,
		NgbDatepickerModule,
	],
	providers: [
		...fromService.services,
		...fromPipes.pipes,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: MsalInterceptor,
			multi: true,
		},
		{
			provide: MSAL_INSTANCE,
			useFactory: MSALInstanceFactory,
		},
		{
			provide: MSAL_GUARD_CONFIG,
			useFactory: MSALGuardConfigFactory,
		},
		{
			provide: MSAL_INTERCEPTOR_CONFIG,
			useFactory: MSALInterceptorConfigFactory,
		},
		MsalService,
		MsalGuard,
		MsalBroadcastService,
		RoleGuard,
		NgbActiveModal,
	],
	bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
